import { Injectable } from '@angular/core';
import {
  DesktopNotificationPrefsI,
  DesktopNotificationTypeT,
} from '@interfaces/user-preferences.interface';
import { UserPreferencesService } from '@modules/users/user.preferences.service';

export interface DesktopNotifyParams {
  type: DesktopNotificationTypeT;
  title: string;
  body?: string;
  icon?: string;
  // Notifications sharing a tag replace each other (e.g. same chat) instead of stacking.
  tag?: string;
  autoCloseMs?: number;
  onClick?: () => void;
  // System alerts (export ready, updates, etc.) fire even when the tab is focused; attendance events don't.
  ignoreFocus?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DesktopNotificationService {
  private readonly defaultAutoCloseMs = 20000;
  private prefs?: DesktopNotificationPrefsI;

  constructor(private readonly userPreferencesService: UserPreferencesService) {
    this.userPreferencesService.userPreferences$.subscribe((preferences) => {
      this.prefs = preferences?.desktopNotifications;
    });
  }

  public get isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  public get permission(): NotificationPermission | 'unsupported' {
    return this.isSupported ? Notification.permission : 'unsupported';
  }

  public async requestPermission(): Promise<NotificationPermission | 'unsupported'> {
    if (!this.isSupported) {
      return 'unsupported';
    }
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      return Notification.permission;
    }
    return Notification.requestPermission();
  }

  // Sample notification for the settings "test" button — requests permission and fires regardless of
  // the focus guard and per-type preferences (the user explicitly asked for it). Returns false if blocked.
  public async test(title: string, body: string): Promise<boolean> {
    const permission = await this.requestPermission();
    if (permission !== 'granted' || !this.isSupported) {
      return false;
    }
    try {
      const notification = new Notification(title, { body, icon: this.defaultIcon, tag: 'omni-desktop-test' });
      setTimeout(() => notification.close(), 6000);
      return true;
    } catch {
      return false;
    }
  }

  public notify(params: DesktopNotifyParams): void {
    if (!this.isSupported || Notification.permission !== 'granted') {
      return;
    }
    // Mirror WhatsApp Web: only surface an OS notification when the user is not already looking.
    // System alerts opt out of this guard via ignoreFocus.
    if (!params.ignoreFocus && !document.hidden && document.hasFocus()) {
      return;
    }
    if (!this.isTypeEnabled(params.type)) {
      return;
    }

    try {
      const notification = new Notification(params.title, {
        body: params.body,
        icon: params.icon ?? this.defaultIcon,
        tag: params.tag,
      });
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        params.onClick?.();
        notification.close();
      };
      const autoCloseMs = params.autoCloseMs ?? this.defaultAutoCloseMs;
      if (autoCloseMs > 0) {
        setTimeout(() => notification.close(), autoCloseMs);
      }
    } catch {
      // Notification constructor throws on platforms that require a ServiceWorker registration; ignore.
    }
  }

  private get defaultIcon(): string {
    return `${window.location.origin}/assets/images/logo-omnismart.jpg`;
  }

  private isTypeEnabled(type: DesktopNotificationTypeT): boolean {
    if (!this.prefs) {
      return true;
    }
    if (this.prefs.enabled === false) {
      return false;
    }
    return this.prefs[type] !== false;
  }
}
